import React from 'react';
import { useSelector } from 'react-redux';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/system';
import Box from '@mui/material/Box';
import StarIcon from 'assets/icons/star.svg?react';
import StarCheckedIcon from 'assets/icons/star_checked.svg?react';
import CopyUrlButton from 'components/CopyUrlButton';
import separateThousands from 'helpers/separateThousands';
import { COLUMNS_LIMIT } from 'variables/main';
import DefaultImage from 'assets/images/profession-default.jpg';
import { Link } from 'react-router-dom';
import { Gamepad2 } from 'lucide-react';
import { getLocalizedPath } from 'helpers/getLocale';
import { getSimulator } from 'pages/Simulator/catalog';
import professionCases from 'helpers/professionCases';
const Breadcrumbs = React.lazy(() => import('components/BreadCrumbs'));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: 10,
  color: theme.palette.textPrimary.main,
  [theme.breakpoints.down('md')]: {
    fontSize: 32,
    lineHeight: '41px',
    fontWeight: 600,
    marginBottom: 10
  }
}));

const Description = styled(Typography)(({ theme }) => ({
  color: theme.palette.textPrimary.main,
  opacity: 0.8,
  fontSize: 21,
  fontWeight: 500,
  marginBottom: 15,
  [theme.breakpoints.down('md')]: {
    fontSize: 18,
    lineHeight: '28px'
  }
}));

const Preview = styled('div')(({ theme }) => ({
  width: 505,
  height: 350,
  position: 'relative',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: 15,
    overflow: 'hidden'
  },
  [theme.breakpoints.down('lg')]: {
    width: 250,
    height: 'auto'
  },
  // Tablet + phone: full-bleed image (no side padding), no bottom gap so the card overlaps it.
  [theme.breakpoints.down('md')]: {
    width: 'calc(100% + 40px)',
    marginLeft: -20,
    marginRight: -20,
    marginBottom: 0,
    '& img': {
      borderRadius: 0
    }
  }
}));

const ImagePlaceholder = styled('div')(({ theme }) => ({
  width: '100%',
  height: '100%',
  borderRadius: 15,
  [theme.breakpoints.down('lg')]: {
    width: 250,
    marginRight: 40,
    height: 'auto'
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
    marginRight: 0,
    marginBottom: 15,
    borderRadius: 0
  },
  [theme.breakpoints.down('sm')]: {
    marginLeft: -20,
    width: 'calc(100% + 40px)',
    '& img': {
      borderRadius: 0
    }
  }
}));

const ShortInfo = styled('div')(({ theme }) => ({
  display: 'flex',
  marginTop: 25,
  marginBottom: 25,
  gap: 20,
  // Tablet + phone: drop the top margin so the full-bleed image sits flush
  // under the header (no white gap above it).
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: 0,
    marginTop: 0
  }
}));

const BoxSidebar = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  flex: 2,
  display: 'flex',
  flexDirection: 'column',
  padding: '25px 25px',
  borderRadius: 15,
  // Tablet + phone: pull the card up over the image bottom; square the top
  // corners so it sits flush on the image, keep the bottom corners rounded.
  [theme.breakpoints.down('md')]: {
    flex: 1,
    marginTop: -80,
    borderRadius: '0 0 15px 15px',
    position: 'relative',
    zIndex: 1
  },
  // Phone overlaps a bit less than tablet (smaller image).
  [theme.breakpoints.down('sm')]: {
    marginTop: -56
  }
}));

const BoxSidebarLeft = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  [theme.breakpoints.down('md')]: {
    flex: 1
  }
}));

const Tags = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  marginBottom: 15,
  fontSize: 21
}));

const ButtonTag = styled(Button)(({ theme }) => ({
  color: theme.palette.tertiary.main,
  lineHeight: '11px',
  fontSize: 16,
  '&:hover': {
    backgroundColor: 'transparent'
  }
}));

const Actions = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  rowGap: 12
}));

// Sits next to the share button; same size, secondary (yellow) fill.
const SimulatorButton = styled(Button)(({ theme }) => ({
  color: theme.palette.black.main,
  backgroundColor: theme.palette.secondary.main,
  padding: '6px 24px 6px 25px',
  fontSize: 16,
  lineHeight: '22px',
  fontWeight: 500,
  boxShadow: 'none',
  marginRight: 20,
  borderRadius: 8,
  '&:hover': { backgroundColor: theme.palette.secondary.dark, boxShadow: 'none' }
}));

const ShortInfoItems = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'start',
  marginBottom: 40,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    marginBottom: 15
  }
}));

const ShortInfoValue = styled(Typography)(({ theme, decreasing }) => ({
  color: theme.palette.textPrimary.main,
  fontSize: 24,
  lineHeight: '36px',
  fontWeight: 700,
  '& span': {
    fontSize: 18,
    lineHeight: '18px',
    paddingLeft: 10,
    fontWeight: 400,
    color: decreasing === 'true' ? theme.palette.error.main : theme.palette.primary.main
  }
}));

const ShortInfoItem = styled('div')(({ theme }) => ({
  width: 186,
  marginRight: 24,
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    marginRight: 0,
    marginBottom: 25
  }
}));

const ShortInfoTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.textPrimary.main,
  fontSize: 16,
  opacity: 0.8,
  lineHeight: '24px',
  fontWeight: 400
}));

const FavoriteButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 20,
  right: 20,
  [theme.breakpoints.down('lg')]: {
    top: 10,
    right: 45
  },
  [theme.breakpoints.down('md')]: {
    top: 25,
    right: 25
  }
}));

const ShortInfoComponent = ({
  t,
  professionInfo,
  handleToggleFavorite,
  isFavorite,
  handleChooseTag,
  breadcrumbs,
  loading,
  slides
}) => {
  const statistic = professionInfo.jobStatistic;
  const simulator = getSimulator(professionInfo?._id);
  const vacanciesCount = statistic?.vacanciesCount?.value;
  const vacanciesDynamics = statistic?.vacanciesDynamics?.value;
  const salaryAverage = statistic?.salaryAverage?.value;
  const employmentOdds = statistic?.employmentOdds?.value;
  const hasVacancies = Number.isFinite(vacanciesCount);
  const hasSalary = Number.isFinite(salaryAverage) && salaryAverage > 0;
  const hasEmploymentOdds =
    employmentOdds !== undefined && employmentOdds !== null && employmentOdds !== '';
  const hasMarketStatistics = hasVacancies || hasSalary || hasEmploymentOdds;

  const isMobile = useSelector((state) => state.app.isMobile);

  const [validatedImage, setValidatedImage] = React.useState(null);

  React.useEffect(() => {
    if (!professionInfo?.photoLinks?.main) {
      setValidatedImage(DefaultImage);
      return;
    }

    const img = new Image();
    const fullSrc = `${professionInfo.photoLinks.main}/w505h350`;

    img.onload = () => setValidatedImage(fullSrc);
    img.onerror = () => setValidatedImage(DefaultImage);

    img.src = fullSrc;
  }, [professionInfo?.photoLinks?.main]);

  return (
    <>
      {slides || isMobile ? null : <Breadcrumbs breadcrumbs={breadcrumbs} />}

      <ShortInfo>
        <BoxSidebarLeft>
          <Preview>
            {loading || !validatedImage ? (
              <ImagePlaceholder />
            ) : (
              <img src={validatedImage} alt={professionInfo.name} width={505} height={350} />
            )}
            <FavoriteButton onClick={handleToggleFavorite} aria-label="favorite" size="small">
              {isFavorite ? <StarCheckedIcon /> : <StarIcon />}
            </FavoriteButton>
          </Preview>
        </BoxSidebarLeft>

        <BoxSidebar>
          <Title variant="h1">{professionInfo.name}</Title>
          <Tags>
            {(professionInfo?.tags || []).map((tag, index) => {
              if (index >= COLUMNS_LIMIT) return null;
              return (
                <ButtonTag
                  key={tag?._id}
                  onClick={() => handleChooseTag(tag?._id || tag)}
                  aria-label={tag.value || tag}
                  style={{ padding: 5 }}>
                  #{tag.value || tag}
                </ButtonTag>
              );
            })}
          </Tags>
          <Description>{professionInfo.descriptionShort}</Description>
          {hasMarketStatistics ? (
            <ShortInfoItems>
              {hasVacancies ? (
                <ShortInfoItem>
                  <ShortInfoValue decreasing={(vacanciesDynamics < 0).toString()}>
                    {vacanciesCount}
                    {Number.isFinite(vacanciesDynamics) ? (
                      <span>{`${vacanciesDynamics > 0 ? '+' : ''}${vacanciesDynamics}%`}</span>
                    ) : null}
                  </ShortInfoValue>
                  <ShortInfoTitle>
                    {t('VacanciesCount', professionCases(professionInfo, 'genitive'))}
                  </ShortInfoTitle>
                </ShortInfoItem>
              ) : null}
              {hasSalary ? (
                <ShortInfoItem>
                  <ShortInfoValue>{`${salaryAverage}₴`}</ShortInfoValue>
                  <ShortInfoTitle>
                    {t('SalaryMedium', {
                      ...professionCases(professionInfo, 'genitive'),
                      name: separateThousands(professionInfo?.declensions?.genitive || '')
                    })}
                  </ShortInfoTitle>
                </ShortInfoItem>
              ) : null}
              {hasEmploymentOdds ? (
                <ShortInfoItem>
                  <ShortInfoValue>{t(employmentOdds)}</ShortInfoValue>
                  <ShortInfoTitle>{t('Competition')}</ShortInfoTitle>
                </ShortInfoItem>
              ) : null}
            </ShortInfoItems>
          ) : null}
          <Actions>
            <CopyUrlButton authRequired={false} />
            {simulator && !slides && (
              <SimulatorButton
                component={Link}
                to={getLocalizedPath(`/simulator/${simulator.id}`)}
                variant="contained"
                endIcon={<Gamepad2 size={20} />}>
                {t('TrySimulator')}
              </SimulatorButton>
            )}
          </Actions>
        </BoxSidebar>
      </ShortInfo>
    </>
  );
};

export default ShortInfoComponent;
